/**
 * Store quản lý thông tin đăng nhập của user bao gồm
 * - Thông tin user
 * - JWT - JSON Web Token
 * - Role
 * - Profile
 * - Policies
 */

import alert from '@/plugins/alert'
import { vapidKey } from '@/helpers/firebase.js'
import router from '@/router'
import { Auth, Staff, Teacher, Policies } from '@/plugins/api'
import { get, zipObject, fill } from 'lodash'
import firebase from 'firebase/app'

export default {
  namespaced: true,
  state: {
    user: null,
    role: {},
    policies: {},
    profile: {},
    isAuthenticated: false,
    jwt: null
  },
  actions: {
    async signIn({ dispatch, commit }, { identifier = '', password = '' } = {}) {
      try {
        const { jwt, user, policies } = await Auth.signIn({ identifier, password })
        commit('setUser', user)
        commit('setJwt', jwt)
        commit('setRole', get(user, 'role', {}))
        dispatch('setAuthPolicies', policies)
        const messaging = firebase.messaging()
        messaging
          .getToken({ vapidKey })
          .then(currentToken => {
            if (currentToken) Staff.subNotify({ token: currentToken })
            else console.log('No registration token available. Request permission to generate one.')
          })
          .catch(() => {
            console.log('*** An error occurred while retrieving token firebase. ')
          })
      } catch (error) {
        console.error('signIn', error)
      }
    },
    async signOut({ commit }) {
      await Staff.unSubNotify()
      commit('clearUserData')
      router.push('/signIn')
    },
    async fetchStaff({ commit }, query) {
      commit('setProfile', await Staff.fetch(query))
    },
    async fetchTeacher({ commit }, query) {
      commit('setProfile', await Teacher.fetch(query))
    },
    async forgotPassword(context, email) {
      await Auth.forgotPassword(email)
      alert.success('Hãy kiểm tra mail của bạn')
    },
    async resetPassword({ commit }, { code, password, passwordConfirmation }) {
      const { user, jwt } = await Auth.resetPassword(code, password, passwordConfirmation)
      commit('setUser', user)
      commit('setJwt', jwt)
      router.push('/dashboard')
    },
    setAuthPolicies({ commit }, policies) {
      const policiesObj = zipObject(policies, fill(Array(policies.length), true))
      commit('setPolicies', policiesObj)
    },
    setAvatar({ commit }, avatar) {
      commit('setAvatar', avatar)
    },
    async checkAuthPolices({ commit }, { roleId }) {
      const id = roleId
      const rolePolicies = await Policies.checkAuthPolices(id)
      const listPolicies = rolePolicies[0].policies
      let policies = {}
      await listPolicies.forEach(element => {
        policies[element.code] = true
      })
      commit('setPolicies', policies)
    }
  },
  mutations: {
    setUser(state, user) {
      state.user = user
      if (user) {
        state.role = user.role
        state.isAuthenticated = true
      } else {
        state.isAuthenticated = false
        state.role = null
      }
    },
    setJwt(state, jwt) {
      state.jwt = jwt
    },
    setRole(state, role) {
      state.role = role
    },
    setProfile(state, profile) {
      state.profile = get(profile, '0', {})
    },
    setPolicies(state, policies) {
      state.policies = { ...policies }
    },
    clearUserData(state) {
      state.user = null
      state.jwt = null
      state.profile = {}
      state.role = {}
      state.policies = {}
      state.isAuthenticated = false
    },
    setAvatar(state, avatar) {
      state.profile = {
        ...state.profile,
        avatar: avatar
      }
      state.avatar = avatar
    }
  },
  getters: {
    user(state) {
      return !state.user ? {} : state.user
    },
    role(state) {
      return state.role || state.user.role || {}
    },
    profile(state) {
      return !state.profile ? {} : state.profile
    },
    ownClassIds(state) {
      if (state.role.type === 'teacher' && state.profile.ownClasses && state.profile.ownClasses.length) {
        return state.profile.ownClasses.map(c => c.id)
      }
      return null
    },
    ownGradeIds(state) {
      if (state.role.type === 'teacher' && state.profile.ownClasses && state.profile.ownClasses.length) {
        return state.profile.ownClasses.map(c => c.grade)
      }
      return null
    },
    isEditDocument(state) {
      return state.role.type !== 'teacher' && state.role.type !== 'inspector'
    }
  }
}
